import { useEffect, useRef, useState } from "react";
import { ApiRequestError, apiRequest } from "../api";
import { SessionAction, Shell, StatusPill } from "../components";
import { setPageMetadata } from "../page-metadata";

const BOMANA_DOWNLOAD_URL = "https://bomana.ruikang.wang";
const LOGIN_URL = "/?access=login&returnTo=%2Faccount%23orders";
const MAX_CHECKS = 20;
const CHECK_INTERVAL_MS = 2_000;

const STAGE_COPY = {
  VERIFYING: {
    label: "正在核验", title: "正在确认付款", tone: "warn",
    message: "正在确认付款…",
  },
  FULFILLED: {
    label: "已到账", title: "权益已到账", tone: "good",
    message: "付款已确认。",
  },
  PENDING: {
    label: "等待确认", title: "付款仍在确认中", tone: "warn",
    message: "付款仍在确认中，无需重复支付。",
  },
  PAID: {
    label: "已付款", title: "权益仍在确认中", tone: "warn",
    message: "订单已记录付款，权益仍在确认中，无需重复支付。",
  },
  INVALID: {
    label: "无法识别订单", title: "无法确认这笔订单", tone: "neutral",
    message: "没有找到订单信息，请到账户中心查看订单。",
  },
  AUTH_REQUIRED: {
    label: "需要登录", title: "请登录后查看订单", tone: "neutral",
    message: "登录状态已失效或尚未登录，请登录购买时使用的账户，在订单记录中查看结果。",
  },
  NOT_FOUND: {
    label: "未找到订单", title: "当前账户下未找到这笔订单", tone: "neutral",
    message: "订单可能不存在，或不属于当前账户。请确认订单信息和购买时使用的账户。",
  },
  ERROR: {
    label: "查询失败", title: "暂时无法查询订单", tone: "warn",
    message: "网络或服务暂时不可用，尚无法确认订单结果。请稍后重试，无需重复支付。",
  },
  UNAVAILABLE: {
    label: "无法查询", title: "无法查看这笔订单", tone: "neutral",
    message: "订单查询未能完成，请到账户中心核对订单，或联系支持。无需重复支付。",
  },
  UNKNOWN: {
    label: "状态待核实", title: "暂时无法识别订单状态", tone: "warn",
    message: "请稍后刷新状态，或到账户中心核对订单。无需重复支付。",
  },
  CANCELLED: {
    label: "已取消", title: "订单已取消", tone: "neutral",
    message: "这笔订单已取消。如果支付宝已有扣款，请到账户中心核对记录或联系支持，无需重复支付。",
  },
  CLOSED: {
    label: "已关闭", title: "订单已关闭", tone: "neutral",
    message: "这笔订单已关闭。如果支付宝已有扣款，请到账户中心核对记录或联系支持，无需重复支付。",
  },
  REFUNDED: {
    label: "已退款", title: "订单已退款", tone: "neutral",
    message: "订单记录显示已退款，实际退款金额和到账情况请查看账户及支付宝记录。",
  },
  REFUND_REQUESTED: {
    label: "已申请退款", title: "退款申请待处理", tone: "warn",
    message: "这笔订单已申请退款，尚不代表退款完成。请到账户中心查看处理进度。",
  },
  MANUAL_REVIEW: {
    label: "待人工核对", title: "订单需要人工核对", tone: "warn",
    message: "这笔订单需要人工核对，请到账户中心查看记录或联系支持，无需重复支付。",
  },
} as const;
type PaymentStage = keyof typeof STAGE_COPY;
interface PaymentView {
  stage: PaymentStage;
  checking: boolean;
  bomanaReady: boolean;
  message?: string;
}
interface PaymentOrder {
  appId: string;
  // The API uses text statuses, including CLOSED and REFUND_REQUESTED.
  orderStatus: string;
  pendingRecoveryEmail: string | null;
  recoveryEmailActivatedAt: string | null;
}

function checkingView(): PaymentView {
  return { stage: "VERIFYING", checking: true, bomanaReady: false };
}

export function PayDonePage() {
  // Keep a fallback order stable when success clears session storage.
  const [lastOrderId] = useState(() => sessionStorage.getItem("cheemspay-last-order"));
  const orderId = new URLSearchParams(window.location.search).get("order_id") ?? lastOrderId;
  return <PaymentResult key={orderId ?? ""} orderId={orderId} />;
}

function PaymentResult({ orderId }: { orderId: string | null }) {
  const [view, setView] = useState<PaymentView>(checkingView);
  const [retry, setRetry] = useState(0);
  const openedBomanaRef = useRef(false);

  useEffect(() => {
    setPageMetadata(
      "付款结果 | CheemsPay",
      "核验 CheemsPay 订单的付款与权益状态，并提供后续处理入口。",
    );
  }, []);

  useEffect(() => {
    if (!orderId) {
      setView({ stage: "INVALID", checking: false, bomanaReady: false });
      return;
    }
    const controller = new AbortController();
    let timer: number | undefined;
    let attempts = 0;
    const finish = (stage: PaymentStage) => {
      setView({ stage, checking: false, bomanaReady: false });
    };
    const check = async () => {
      let order: PaymentOrder;
      try {
        const response = await apiRequest<{ order: PaymentOrder }>(
          `/api/orders/${encodeURIComponent(orderId)}`,
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        order = response?.order;
      } catch (error) {
        if (controller.signal.aborted) return;
        finish(error instanceof ApiRequestError
          ? error.status === 401 ? "AUTH_REQUIRED"
            : error.status === 404 ? "NOT_FOUND"
              : error.status >= 500 || error.status === 408 || error.status === 429 ? "ERROR"
                : "UNAVAILABLE"
          : "ERROR");
        return;
      }

      if (order?.orderStatus === "FULFILLED") {
        const isBomanaOrder = order.appId === "bomana";
        const successMessage = isBomanaOrder
          ? "付款已确认。如下载页未打开，请点击下方按钮。"
          : "付款已确认。";
        setView({ stage: "FULFILLED", checking: false, bomanaReady: isBomanaOrder, message: successMessage });
        if (isBomanaOrder && !openedBomanaRef.current) {
          openedBomanaRef.current = true;
          window.open(BOMANA_DOWNLOAD_URL, "_blank", "noopener,noreferrer");
        }
        // A newer checkout may have saved a different order while this request ran.
        if (sessionStorage.getItem("cheemspay-last-order") === orderId) {
          sessionStorage.removeItem("cheemspay-last-order");
        }
        if (order.pendingRecoveryEmail && !order.recoveryEmailActivatedAt) {
          const binding = await apiRequest(
            `/api/orders/${encodeURIComponent(orderId)}/recovery-email`,
            { method: "POST", signal: controller.signal },
          ).then(() => true, () => false);
          if (controller.signal.aborted) return;
          setView({
            stage: "FULFILLED", checking: false, bomanaReady: isBomanaOrder,
            message: binding
              ? isBomanaOrder
                ? "付款已确认，验证邮件已发出。如下载页未打开，请点击下方按钮。"
                : "付款已确认，验证邮件已发出。"
              : isBomanaOrder
                ? successMessage
                : "付款已确认。邮箱绑定可在账户中继续完成。",
          });
        }
        return;
      }

      switch (order?.orderStatus) {
        case "CREATED":
        case "PENDING_PAYMENT":
        case "PAID": {
          attempts += 1;
          const checking = attempts < MAX_CHECKS;
          setView({
            stage: order.orderStatus === "PAID" ? "PAID" : checking ? "VERIFYING" : "PENDING",
            checking,
            bomanaReady: false,
          });
          if (checking) timer = window.setTimeout(() => void check(), CHECK_INTERVAL_MS);
          return;
        }
        case "CANCELLED":
        case "CLOSED":
        case "REFUNDED":
        case "REFUND_REQUESTED":
        case "MANUAL_REVIEW":
          finish(order.orderStatus);
          return;
        default:
          finish("UNKNOWN");
      }
    };
    setView(checkingView());
    void check();
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [orderId, retry]);

  const stage = STAGE_COPY[view.stage];
  const canRetry = ["PENDING", "PAID", "ERROR", "UNKNOWN", "REFUND_REQUESTED", "MANUAL_REVIEW"].includes(view.stage);
  const needsRecoveryActions = view.stage !== "VERIFYING" && view.stage !== "FULFILLED";
  const isError = ["INVALID", "AUTH_REQUIRED", "NOT_FOUND", "ERROR", "UNAVAILABLE", "UNKNOWN"].includes(view.stage);

  return (
    <Shell
      action={<SessionAction signedOutReturnTo="/account#orders" />}
      back={{ href: "/account", label: "返回账户" }}
    >
      <section className="single-card-page">
        <div aria-busy={view.checking} className="single-card centered">
          <StatusPill tone={stage.tone}>{stage.label}</StatusPill>
          <h1>{stage.title}</h1>
          <p role={isError ? "alert" : "status"}>{view.message ?? stage.message}</p>
          <a className="button anchor-button" href={view.stage === "AUTH_REQUIRED" ? LOGIN_URL : "/account"}>
            {view.stage === "AUTH_REQUIRED" ? "登录并查看订单" : "查看账户中心"}
          </a>
          {view.bomanaReady ? (
            <a className="button anchor-button" href={BOMANA_DOWNLOAD_URL} rel="noopener noreferrer" target="_blank">
              前往 Bomana 官网下载
            </a>
          ) : null}
          {needsRecoveryActions ? (
            <div className="decision-row" aria-label="订单处理选项">
              {canRetry ? (
                <button className="button secondary" type="button" disabled={view.checking} onClick={() => {
                  setView(checkingView());
                  setRetry((value) => value + 1);
                }}>
                  刷新状态
                </button>
              ) : null}
              <a className="button secondary" href="/contact">联系支持</a>
            </div>
          ) : null}
        </div>
      </section>
    </Shell>
  );
}
